import api from '@/api'
import Vue from 'vue';
import permissions from '@/api/permissions'
import LayoutConverter from '@/libs/layout-converter'
import UserState from "../api/types/user-state";

export default {
  state: {
    users: {
      filters: {},
      total: undefined,
      items: []
    },

    accreditations: {
      filters: {},
      total: undefined,
      items: []
    },

    contractors: {
      filters: {},
      total: undefined,
      items: []
    },

    lots: {
      filters: {},
      total: undefined,
      items: []
    },

    mentionedLots: {
      filters: {},
      total: undefined,
      items: []
    },

    publicLots: {
      filters: {},
      total: undefined,
      items: []
    },

    tenders: {
      filters: {},
      total: undefined,
      items: []
    },

    favorites: {
      filters: {},
      total: undefined,
      items: []
    }
  },

  mutations: {
    setSearchResults: (s, data) => {
      const doConcat = !!data.doConcat;
      const section = s[data.section];
      const results = data.results;

      section.filters = results.filters;
      section.total = results.total;

      if (section.filters.page === 1) {
        section.items = [];
      }

      for (let i = 0; i < results.items.length; ++i) {
        if (doConcat) {
          section.items.push(results.items[i]);
        }
        else {
          const index = section.items.findIndex(e => e.id === results.items[i].id);

          if (index !== -1) {
            Vue.set(section.items, index, results.items[i]);
          }
          else {
            section.items.push(results.items[i]);
          }
        }
      }
    },
  },

  actions: {
    async fetchUsers({dispatch}, {
      from,
      to,
      showCustomerUsers,
      showContractorUsers,
      showMainUsers,
      showCommissionUsers,
      departments,
      organizations,
      users,
      userStates,
      userAccreditationState,
      state,
      mainUsers,
      inCommission
    }) {
      try {
        let roles = [];

        if (showCustomerUsers) {
          roles.push(api.UserRoleType.CUSTOMER);
        }

        if (showContractorUsers) {
          roles.push(api.UserRoleType.CONTRACTOR);
        }

        if (showMainUsers) {
          roles.push(api.UserRoleType.MAIN);
        }

        if (showCommissionUsers) {
          roles.push(api.UserRoleType.COMMISSION);
        }

        if (from instanceof Date) {
          from.setHours(0);
          from.setMinutes(0);
          from.setSeconds(0);
        }

        if (to instanceof Date) {
          to.setHours(23);
          to.setMinutes(59);
          to.setSeconds(59);
        }

        const filters = {
          pageSize: 5,
          page: 1,
          dtFrom: +from || undefined,
          dtTo: +to || undefined,
          departments: (departments || []).join(',') || undefined,
          organizations: (organizations || []).join(',') || undefined,
          users: (users || []).join(',') || undefined,
          userStates: (userStates || []).join(',') || undefined,
          mainUsers: (mainUsers || []).join(',') || undefined,
          roles: (roles || []).join(',') || undefined,
          state: api.Enum.toAPI(state),
          userAccreditationState: api.Enum.toAPI(userAccreditationState),
          commission: inCommission
        };

        await dispatch('_fetchUsers', filters)
      }
      catch (e) {
        console.error('Something went wrong while filtering users', e);
        throw e;
      }
    },

    async fetchUsersRefresh({dispatch, state}) {
      await dispatch('_fetchUsers', {
        ...state.users.filters
      });
    },

    async fetchSortedUsers({dispatch, state}, { order }) {
      await dispatch('_fetchUsers', {
        ...state.users.filters,
        page: order.valueOf() !== state.users.filters.dir ? 1: state.users.filters.page,
        dir: order.valueOf()
      });
    },

    async fetchMoreUsers({dispatch, state}) {
      await dispatch('_fetchUsers', {
        ...state.users.filters,
        page: state.users.filters.page + 1
      });
    },

    exportUsersList({state}) {
      api.download('account/users/export', {
        params: {
          ...state.users.filters,
          page: undefined,
          pageSize: undefined
        }
      });
    },

    clearUsersList({commit}) {
      commit('setSearchResults', {
        section: 'users',
        results: {
          filters: {},
          total: undefined,
          items: []
        }
      });
    },

    async _fetchUsers({commit, state}, filters) {
      const response = await api.get('account/users/list', {params: filters});
      const {status, result, errorCode} = response.data;

      if (status === 200) {
        commit('setSearchResults', {
          section: 'users',
          results: {
            filters: filters,
            total: result.total,
            items: result.data.map(e => ({
              ...e,
              state: api.Enum.fromAPI(e.state, api.UserState),
              accreditationState: api.Enum.fromAPI(e.stateCode, api.AccreditationState),
              userAccreditationState: api.Enum.fromAPI(e.userAccreditationState, api.UserAccreditationState),
              role: api.Enum.fromAPI(e.role, api.UserRole),
              inCommission: !!e.commissionMember
            }))
          }
        });
      }
      else {
        throw new api.Errors(errorCode);
      }
    },

    async fetchAccreditations({dispatch, state}, {
      expired,
      showDigitalSignatures,
      showAccreditations,
      showInDraft,
      showInProcessing,
      showInNotActive,
      showInApproved,
      showInDeclined,
      organizations,
      users,
      pageSize,
      page
    }) {
      try {
        let types = [];
        let states = [];

        if (showDigitalSignatures) {
          types.push(api.AccreditationType.DIGITAL_SIGNATURE);
        }

        if (showAccreditations) {
          types.push(api.AccreditationType.ACCREDITATION);
        }

        if (showInDraft) {
          states.push(api.AccreditationState.DRAFT);
        }

        if (showInProcessing) {
          states.push(api.AccreditationState.PROCESSING);
        }

        if (showInNotActive) {
          states.push(api.AccreditationState.NOT_ACTIVE);
        }

        if (showInApproved) {
          states.push(api.AccreditationState.APPROVED);
        }

        if (showInDeclined) {
          states.push(api.AccreditationState.DECLINED);
        }

        if (expired instanceof Date) {
          expired.setHours(23);
          expired.setMinutes(59);
          expired.setSeconds(59);
        }

        const filters = {
          pageSize: pageSize || 5,
          page: page || 1,
          dtExpired: +expired || undefined,
          types: types.join(',') || undefined,
          states: states.join(',') || undefined,
          organizations: (organizations || []).join(',') || undefined,
          users: (users || []).join(',') || undefined,
        };

        await dispatch('_fetchAccreditations', filters);
      }
      catch (e) {
        console.error('Something went wrong while filtering users', e);
        throw e;
      }
    },

    async fetchMoreAccreditations({dispatch, state}) {
      await dispatch('_fetchAccreditations', {
        ...state.accreditations.filters,
        page: state.accreditations.filters.page + 1
      });
    },

    exportAccreditationsList({state}) {
      api.download('account/accreditation/export', {
        params: {
          ...state.accreditations.filters,
          page: undefined,
          pageSize: undefined
        }
      });
    },

    async _fetchAccreditations({commit, state}, filters) {
      const response = await api.get('account/accreditation/list', {params: filters});
      const {status, result, errorCode} = response.data;

      if (status === 200) {
        commit('setSearchResults', {
          section: 'accreditations',
          results: {
            filters: filters,
            total: result.total,
            items: result.data.map(item => ({
              ...item,
              organizationType: new api.OrganizationType(item.organizationType),
              state: new api.AccreditationState(item.state),
              type: new api.AccreditationType(item.type)
            }))
          }
        });
      }
      else {
        throw new api.Errors(errorCode);
      }
    },

    async fetchContractors({commit, state}, {
      from,
      to,
      organizations,
      personStatus,
      pageSize,
      page
    }) {
      try {
        if (from instanceof Date) {
          from.setHours(0);
          from.setMinutes(0);
          from.setSeconds(0);
        }

        if (to instanceof Date) {
          to.setHours(23);
          to.setMinutes(59);
          to.setSeconds(59);
        }

        const filters = {
          pageSize: pageSize || 100,
          page: page || 1,
          dtFrom: +from || undefined,
          dtTo: +to || undefined,
          organizations: (organizations || []).join(',') || undefined,
          resident:  personStatus !== undefined ? personStatus === api.PersonStatus.RESIDENT : undefined
        };

        const response = await api.get('organization/list', {params: filters});
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          commit('setSearchResults', {
            section: 'contractors',
            results: {
              filters: filters,
              total: result.total,
              items: result.data.map(e => ({
                ...e,
                organizationType: new api.OrganizationType(e.organizationType),
              }))
            }
          });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        console.error('Something went wrong while filtering contractors', e);
        throw e;
      }
    },

    async fetchMoreContractors({dispatch, state}) {
      await dispatch('fetchContractors', { page: state.contractors.filters.page + 1});
    },

    exportContractorsList({state}) {
      const filters = {
        dt: state.contractors.filters.dt
      };

      api.download('organization/export', { params: filters });
    },

    async fetchLots({dispatch, state}, {
      publicationDateFrom,
      publicationDateTo,
      creationDateFrom,
      creationDateTo,
      priceFrom,
      priceTo,
      currency,
      owner,
      mainOwner,
      lotNumbers,
      lotSubject,
      lotStates,
      pageSize,
      page
    }) {
      try {
        if (publicationDateFrom instanceof Date) {
          publicationDateFrom.setHours(0);
          publicationDateFrom.setMinutes(0);
          publicationDateFrom.setSeconds(0);
        }

        if (publicationDateTo instanceof Date) {
          publicationDateFrom.setHours(23);
          publicationDateFrom.setMinutes(59);
          publicationDateFrom.setSeconds(59);
        }

        if (creationDateFrom instanceof Date) {
          creationDateFrom.setHours(0);
          creationDateFrom.setMinutes(0);
          creationDateFrom.setSeconds(0);
        }

        if (creationDateTo instanceof Date) {
          creationDateTo.setHours(23);
          creationDateTo.setMinutes(59);
          creationDateTo.setSeconds(59);
        }

        const filters = {
          pageSize: pageSize || 5,
          page: page || 1,
          dtPublicationFrom: +publicationDateFrom || undefined,
          dtPublicationTo: +publicationDateTo || undefined,
          dtCreateFrom: +creationDateFrom || undefined,
          dtCreateTo: +creationDateTo || undefined,
          summFrom: +priceFrom || undefined,
          summTo: +priceTo || undefined,
          currency: currency || undefined,
          user: owner || undefined,
          exactUser: mainOwner || undefined,
          lotNums: (lotNumbers || []).join(',') || undefined,
          query: LayoutConverter.en(lotSubject) || undefined,
          query2: LayoutConverter.ru(lotSubject) || undefined,
          states: (lotStates || []).join(',') || undefined
        };

        await dispatch('_fetchLots', filters);
      }
      catch (e) {
        console.error('Something went wrong while filtering contractors', e);
        throw e;
      }
    },

    async fetchSortedLots({dispatch, state}, { order }) {
      await dispatch('_fetchLots', {
        ...state.lots.filters,
        page: order.valueOf() !== state.lots.filters.dir ? 1: state.lots.filters.page,
        dir: order.valueOf()
      });
    },

    async fetchMoreLots({dispatch, state}) {
      await dispatch('_fetchLots', {
        ...state.lots.filters,
        page: state.lots.filters.page + 1
      });
    },

    exportLotsList({state}) {
      api.download('/lots/export', {
        params: {
          ...state.lots.filters,
          page: undefined,
          pageSize: undefined
        }
      });
    },

    async _fetchLots({commit, state}, filters) {
      const response = await api.get('/lots/list', {params: filters});
      const {status, result, errorCode} = response.data;

      if (status === 200) {
        commit('setSearchResults', {
          section: 'lots',
          results: {
            filters: filters,
            total: result.total,
            items: result.data.map(e => ({
              ...e,
              isFilled: !!e.filled,
              permissions: permissions.init(e.permissions),
              state: new api.LotState(e.state)
            }))
          }
        });
      }
      else {
        throw new api.Errors(errorCode);
      }
    },



    async fetchMentionedLots({commit, state}, {publicationDateFrom, publicationDateTo, creationDateFrom, creationDateTo, priceFrom, priceTo, currency, owner, states, pageSize, page}) {
      try {
        if (publicationDateFrom instanceof Date) {
          publicationDateFrom.setHours(0);
          publicationDateFrom.setMinutes(0);
          publicationDateFrom.setSeconds(0);
        }

        if (publicationDateTo instanceof Date) {
          publicationDateFrom.setHours(23);
          publicationDateFrom.setMinutes(59);
          publicationDateFrom.setSeconds(59);
        }

        if (creationDateFrom instanceof Date) {
          creationDateFrom.setHours(0);
          creationDateFrom.setMinutes(0);
          creationDateFrom.setSeconds(0);
        }

        if (creationDateTo instanceof Date) {
          creationDateTo.setHours(23);
          creationDateTo.setMinutes(59);
          creationDateTo.setSeconds(59);
        }

        const filters = {
          pageSize: pageSize || 5,
          page: page || 1,
          dtPublicationFrom: +publicationDateFrom || state.mentionedLots.filters.dtPublicationFrom ,
          dtPublicationTo: +publicationDateTo || state.mentionedLots.filters.dtPublicationTo,
          dtCreateFrom: +creationDateFrom || state.mentionedLots.filters.dtCreateFrom,
          dtCreateTo: +creationDateTo || state.mentionedLots.filters.dtCreateTo,
          summFrom: +priceFrom || state.mentionedLots.filters.summFrom,
          summTo: +priceTo || state.mentionedLots.filters.summTo,
          currency: currency || state.mentionedLots.filters.currency,
          user: owner || state.mentionedLots.filters.user,
          states: (states || state.mentionedLots.filters.states || []).join(',')
        };

        const response = await api.get('/lots/list/myLots', {params: filters});
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          commit('setSearchResults', {
            section: 'mentionedLots',
            results: {
              filters: filters,
              total: result.total,
              items: result.data.map(e => ({
                ...e,
                permissions: permissions.init(e.permissions),
                requestState: new api.RequestState(e.requestState),
                state: new api.LotState(e.state)
              }))
            }
          });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        console.error('Something went wrong while filtering contractors', e);
        throw e;
      }
    },

    async fetchMoreMentionedLots({dispatch, state}) {
      await dispatch('fetchMentionedLots', { page: state.mentionedLots.filters.page + 1});
    },

    exportMentionedLotsList({state}) {
      const filters = {
        dt: state.lots.filters.dt
      };

      api.download('lots/export', { params: filters });
    },

    async fetchTenders({dispatch, state}, {
      publicationDateFrom,
      publicationDateTo,
      creationDateFrom,
      creationDateTo,
      priceFrom,
      priceTo,
      currency,
      owner,
      mainOwner,
      lotsCountType,
      tenderMethod,
      participationMode,
      tenderSubject,
      tenderNumber,
      pageSize,
      page
    }) {
      try {
        if (publicationDateFrom instanceof Date) {
          publicationDateFrom.setHours(0);
          publicationDateFrom.setMinutes(0);
          publicationDateFrom.setSeconds(0);
        }

        if (publicationDateTo instanceof Date) {
          publicationDateFrom.setHours(23);
          publicationDateFrom.setMinutes(59);
          publicationDateFrom.setSeconds(59);
        }

        if (creationDateFrom instanceof Date) {
          creationDateFrom.setHours(0);
          creationDateFrom.setMinutes(0);
          creationDateFrom.setSeconds(0);
        }

        if (creationDateTo instanceof Date) {
          creationDateTo.setHours(23);
          creationDateTo.setMinutes(59);
          creationDateTo.setSeconds(59);
        }

        const filters = {
          pageSize: pageSize || 5,
          page: page || 1,
          dtPublicationFrom: +publicationDateFrom || undefined,
          dtPublicationTo: +publicationDateTo || undefined,
          dtCreateFrom: +creationDateFrom || undefined,
          dtCreateTo: +creationDateTo || undefined,
          summFrom: +priceFrom || undefined,
          summTo: +priceTo || undefined,
          lotsCount: lotsCountType || undefined,
          currency: currency || undefined,
          owner: owner || undefined,
          exactUser: mainOwner || undefined,
          method: tenderMethod || undefined,
          open: participationMode || undefined,
          num: tenderNumber || undefined,
          query: LayoutConverter.en(tenderSubject) || undefined,
          query2: LayoutConverter.ru(tenderSubject) || undefined
        };

        await dispatch('_fetchTenders', filters);
      }
      catch (e) {
        console.error('Something went wrong while filtering contractors', e);
        throw e;
      }
    },

    async fetchSortedTenders({dispatch, state}, { order }) {
      await dispatch('_fetchTenders', {
        ...state.tenders.filters,
        page: order.valueOf() !== state.tenders.filters.dir ? 1: state.tenders.filters.page,
        dir: order.valueOf()
      });
    },

    async fetchMoreTenders({dispatch, state}) {
      await dispatch('_fetchTenders', {
        ...state.tenders.filters,
        page: state.tenders.filters.page + 1
      });
    },

    exportTendersList({state}) {
      api.download('/tenders/export', {
        params: {
          ...state.tenders.filters,
          page: undefined,
          pageSize: undefined
        }
      });
    },

    async _fetchTenders({commit, state}, filters) {
      const response = await api.get('/tenders/list', {params: filters});
      const {status, result, errorCode} = response.data;

      if (status === 200) {
        commit('setSearchResults', {
          section: 'tenders',
          results: {
            filters: filters,
            total: result.total,
            items: result.data.map(e => ({
              ...e,
              permissions: permissions.init(e.permissions),
              state: new api.TenderState(e.state)
            }))
          }
        });
      }
      else {
        throw new api.Errors(errorCode);
      }
    },

    async fetchPublicLots({dispatch}, {text, states, categories, isArchive, currency, publicationDateFrom, publicationDateTo, priceFrom, priceTo, page}) {
      try {
        const filters = {
          query: LayoutConverter.en(text),
          query2: LayoutConverter.ru(text),
          states: states ? states.join(',') : undefined,
          categories: categories ? categories.join(',') : undefined,
          archive: isArchive,
          currency: currency ? currency.valueOf() : undefined,
          dtPublicationFrom: publicationDateFrom,
          dtPublicationTo: publicationDateTo,
          summFrom: priceFrom,
          summTo: priceTo,
          page: 1,
          pageSize: 10
        };

        await dispatch('_fetchPublicLots', filters);
      }
      catch(e ) {
        console.error('Something went wrong while filtering public lots', e);
        throw e;
      }
    },

    async fetchMorePublicLots({dispatch, state}) {
      const filters = {
        ...state.publicLots.filters,
        page: state.publicLots.filters.page + 1
      };

      await dispatch('_fetchPublicLots', filters);
    },

    async _fetchPublicLots({commit, state}, filters) {
      try {
        const response = await api.get('/tenders/search', {params: filters});
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          commit('setSearchResults', {
            section: 'publicLots',
            results: {
              filters: filters,
              total: result.total,
              items: result.data.map(e => ({
                ...e,
                state: e.countLot === 1 ? new api.LotState(e.state) : new api.TenderState(e.state)
              }))
            },
            doConcat: true
          });
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e ) {
        console.error('Something went wrong while filtering public lots', e);
        throw e;
      }
    },

    async fetchFavorites({commit}, {page, pageSize}) {
      let error;

      try {
        const filters = {
          page: page || 1,
          pageSize: pageSize || 5
        };

        const response = await api.get(`/lots/favorites/${filters.page}/${filters.pageSize}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          commit('setSearchResults', {
            section: 'favorites',
            results: {
              filters: filters,
              total: result.total,
              items: result.data.map(e => ({
                ...e,
                state: new api.LotState(e.state)
              }))
            }
          });
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async fetchMoreFavorites({dispatch, state}) {
      await dispatch('fetchFavorites', {
        ...state.favorites.filters,
        page: state.favorites.filters.page + 1
      });
    },
  }
}
